import { useEffect, useMemo, useRef, useState } from 'react';

import type { CheckoutSessionsResponse } from '../../../services/commonCheckout/types/CheckoutSessionsResponse';
import { getTelephonicEntryUrlDetails } from '../utilities/getTelephonicEntryUrlDetails';
import { isTelephonicEntryReady } from '../utilities/isTelephonicEntryReady';
import { generateTrackEventMetadata } from '../utilities/generateTrackEventMetadata';
import {
  getMerchantId,
  getSycurioTelephonicEntryDetails,
} from '../../../utils/session/selectors';

type SycurioSessionDetailsType = {
  sessionResponse: CheckoutSessionsResponse;
  sycurioPageHeight: string | undefined;
  casiTransactionId: string | undefined;
  checkoutSessionId: string;
};

export const useSycurioSessionDetails = ({
  sessionResponse,
  sycurioPageHeight,
  casiTransactionId,
  checkoutSessionId,
}: SycurioSessionDetailsType) => {
  const sycurioPageMountRef = useRef<number>(0);

  const [
    hasTimeElapseForSycurioInitMessage,
    setHasTimeElapseForSycurioInitMessage,
  ] = useState<boolean>(false);

  const telephonicEntryDetails =
    getSycurioTelephonicEntryDetails(sessionResponse);
  const merchantId = getMerchantId(sessionResponse);

  const isSycurioReady = useMemo(
    () => isTelephonicEntryReady(telephonicEntryDetails),
    [telephonicEntryDetails],
  );

  const { sycurioFragmentUrl = '', sycurioHttpOrigin = '' } =
    useMemo(
      () => getTelephonicEntryUrlDetails(telephonicEntryDetails),
      [telephonicEntryDetails],
    );

  useEffect(() => {
    if (sycurioPageMountRef.current === 0) {
      sycurioPageMountRef.current += 1;
    }

    let timeOutId: ReturnType<typeof setTimeout>;
    if (sycurioPageMountRef.current === 1 && isSycurioReady) {
      timeOutId = setTimeout(() => {
        setHasTimeElapseForSycurioInitMessage(true);
      }, 5000);
    }

    return () => {
      if (timeOutId) {
        clearTimeout(timeOutId);
      }
    };
  }, [isSycurioReady]);

  const appInsightsMetadata = generateTrackEventMetadata({
    sycurioMetadataArgs: {
      sycurioFragmentUrl,
      checkoutSessionId,
      merchantId,
    },
    name: 'cancelFragment',
  });

  /** note: our assumption is if the casi status call is IN_PROGRESS
   *  and we have received the sycurio fragment url, plus
   *  we have received a message from the iframe a page height after 2 seconds
   *  then the iframe has successfully loaded.
   */
  const hasSycurioIframeFailedToLoad = useMemo(
    () =>
      isSycurioReady &&
      sycurioPageMountRef.current === 1 &&
      !sycurioPageHeight &&
      !!sycurioHttpOrigin &&
      hasTimeElapseForSycurioInitMessage,
    [
      isSycurioReady,
      sycurioPageMountRef.current,
      sycurioPageHeight,
      sycurioHttpOrigin,
      hasTimeElapseForSycurioInitMessage,
    ],
  );

  const hasCasiSessionFailed = useMemo(
    () =>
      telephonicEntryDetails?.status === 'FAILED' &&
      telephonicEntryDetails.requestId === casiTransactionId,
    [telephonicEntryDetails?.status, casiTransactionId],
  );
  return {
    isSycurioReady,
    sycurioFragmentUrl,
    sycurioHttpOrigin,
    appInsightsMetadata,
    merchantId,
    sycurioPageMountRef,
    hasSycurioIframeFailedToLoad,
    hasTimeElapseForSycurioInitMessage,
    hasCasiSessionFailed,
  };
};
